/**
 * <TruncatedTextWithTooltip />
 *
 * Renders a single-line, ellipsis-truncated piece of text and only shows
 * a tooltip with the full value when the text is actually overflowing
 * its container (`scrollWidth > clientWidth`). Designed as a drop-in for
 * dense list / table cells.
 *
 * Why a real Radix tooltip instead of the native `title` attribute?
 *   • The `title` attribute renders an OS-styled tooltip that's slow,
 *     hard to read on small text, and isn't accessible via keyboard
 *     focus.
 *   • Radix tooltips respect the app's theme, are keyboard-accessible,
 *     and can wrap long content nicely with a capped width.
 *
 * Usage (most common):
 *
 *   <TruncatedTextWithTooltip text={item.name} />
 *
 * Drop-in replacement for a generic `<span className="truncate">…</span>`:
 *
 *   // before
 *   <span className="truncate font-medium">{item.name}</span>
 *
 *   // after
 *   <TruncatedTextWithTooltip
 *     text={item.name}
 *     className="font-medium"
 *   />
 *
 * Render-prop form when the visible text differs from the tooltip body
 * (e.g. a styled chip plus a description):
 *
 *   <TruncatedTextWithTooltip text={item.description}>
 *     <em>{item.description}</em>
 *   </TruncatedTextWithTooltip>
 *
 * The component is intentionally NOT a layout container — it inherits
 * its width from the parent cell. The parent needs `min-w-0` (and the
 * containing flex/grid track) so the truncation can take effect, just
 * like any other `truncate` element.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { cn } from '../lib/utils';

export const TruncatedTextWithTooltip = ({
  text,
  children,
  className,
  tooltipClassName,
  as: Element = 'span',
  side = 'top',
  align = 'start',
  delayDuration = 200,
  // Hard cap so a 4 KB description doesn't paint a full-screen tooltip.
  tooltipMaxWidth = 480,
  ...rest
}) => {
  const ref = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  // Tooltip Provider lives at the app root, so we don't wrap one here —
  // it would create nested providers and break keyboard navigation.

  const recompute = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // `scrollWidth` reflects the unclipped content width; comparing it to
    // `clientWidth` is the canonical overflow detection idiom. We add a
    // single pixel of tolerance to swallow sub-pixel rounding artifacts
    // (Chrome occasionally reports `scrollWidth = clientWidth + 0.5`).
    setIsOverflowing(el.scrollWidth - el.clientWidth > 1);
  }, []);

  useLayoutEffect(() => {
    recompute();
  }, [recompute, text, children]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [recompute]);

  const visibleContent = children ?? text ?? '';
  const tooltipBody = text ?? (typeof children === 'string' ? children : '');

  const truncated = (
    <Element
      ref={ref}
      // `block truncate` plus `min-w-0` upstream is the standard recipe;
      // we don't force it here so the caller can choose `inline-block`
      // when needed.
      className={cn('truncate', className)}
      // Make focusable when overflowing so keyboard users get the
      // tooltip too. We only flip tabIndex on actual overflow to avoid
      // littering the tab order with thousands of decorative cells.
      tabIndex={isOverflowing ? 0 : undefined}
      {...rest}
    >
      {visibleContent}
    </Element>
  );

  if (!isOverflowing || !tooltipBody) {
    return truncated;
  }

  return (
    <Tooltip delayDuration={delayDuration}>
      <TooltipTrigger asChild>{truncated}</TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        className={cn(
          // Capped width + wrap so long part numbers / descriptions
          // don't paint a single line off-screen.
          'max-w-[var(--ttwt-max-w)] whitespace-normal break-words text-xs leading-relaxed',
          tooltipClassName,
        )}
        style={{ ['--ttwt-max-w']: `${tooltipMaxWidth}px` }}
      >
        {tooltipBody}
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * Wrap a tree (typically a page root) in a TooltipProvider once. Most of
 * the app already does this at the layout root; expose this helper so
 * isolated stories / tests can render the component without boilerplate.
 */
export const TruncatedTooltipProvider = ({ children, delayDuration = 200 }) => (
  <TooltipProvider delayDuration={delayDuration}>{children}</TooltipProvider>
);

export default TruncatedTextWithTooltip;
